// The patient endpoints, in one place.
//
// Every path is practice-scoped: `practice_access_required` reads the practice out
// of the URL and fails closed without one, so there is no unscoped patient URL to
// call by accident. CSRF is attached by the wrapper index.js installs on
// window.fetch, so a plain fetch is correct here.

const API_URL = process.env.REACT_APP_API_URL || "";

// Never surface a server message we did not write. The backend returns
// {error: "..."} for the cases a biller can act on (validation, a coverage the
// EMR owns) and a generic sentence for anything unexpected.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: body.error || fallback, body };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return response.json();
}

function writeOptions(method, payload) {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

// One page of the practice's patients. The response's `total` is every match,
// not the page, so the list can show the practice's count and page through it.
export function searchPatients(practiceId, q, limit = 50, offset = 0) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (q) params.set("q", q);
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/search?${params}`,
    {},
    "Could not load patients.",
  );
}

export function fetchPatientSummary(practiceId, patientId) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/${patientId}/summary`,
    {},
    "Could not load this patient.",
  );
}

export function saveDemographics(practiceId, patientId, fields) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/${patientId}/demographics`,
    writeOptions("PUT", fields),
    "Could not save these details.",
  );
}

export function addCoverage(practiceId, patientId, coverage) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/${patientId}/coverages`,
    writeOptions("POST", coverage),
    "Could not add this coverage.",
  );
}

// Term-date a coverage, and optionally append its successor. Never an in-place
// edit: a claim already submitted was billed under the old row and still points
// at it.
export function replaceCoverage(practiceId, coverageId, payload) {
  return request(
    `${API_URL}/api/practices/${practiceId}/coverages/${coverageId}`,
    writeOptions("PUT", payload),
    "Could not update this coverage.",
  );
}

// The practice's configured payers, for the "add a coverage" picker. There is no
// GET /payers endpoint; payers ride along on the practice detail, which is what
// Practice Details reads too.
export async function fetchPracticePayers(practiceId) {
  const practice = await request(
    `${API_URL}/api/practices/${practiceId}`,
    {},
    "Could not load this practice’s payers.",
  );
  return practice.payers || [];
}

// ONE eligibility check for ONE patient, from a person's click. Each call is a paid
// 270/271 to the payer via Stedi, so the caller confirms first and there is no bulk
// version of this function on purpose.
export function checkEligibility(practiceId, patientId) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/${patientId}/eligibility-check`,
    writeOptions("POST", {}),
    "Could not check eligibility.",
  );
}

// --- Duplicates (the staff-only Duplicates panel) -----------------------------
//
// The list is biller-visible; the two merges are admin-only and destructive, so
// every merge body carries `confirm: true` and nothing here posts without a
// person's click in the confirm modal. A 503 means the audit table has not been
// migrated yet; the panel shows the server's sentence and disables merging.

// {exact: [{patients, survivor_id, reason?}], probable: [{patients, why}],
//  skipped: [{patient_ids, reason}], counts: {rows, exact_groups, probable_pairs,
//  skipped_groups}}
export function fetchDuplicates(practiceId) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/duplicates`,
    {},
    "Could not load duplicate patients.",
  );
}

// Merge ONE group. → {survivor_id, merged_ids, moved: {table: n}, filled: [...]}
export function mergePatients(practiceId, patientIds) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/merge`,
    writeOptions("POST", { patient_ids: patientIds, confirm: true }),
    "Could not merge these patients.",
  );
}

// Merge every exact group. `dryRun` true counts without writing, which is what
// the confirm modal shows before the real call.
// → {groups, merged_rows, skipped: [...], counts: {...}, dry_run}
export function mergeExact(practiceId, { dryRun }) {
  return request(
    `${API_URL}/api/practices/${practiceId}/patients/merge-exact`,
    writeOptions("POST", { confirm: true, dry_run: !!dryRun }),
    "Could not merge the exact matches.",
  );
}
